import type { Bordereau, Exercice } from '@mairistem/comptabilite';
import { isNewIdentifiant } from '@mairistem/tools';
import { isEmpty } from 'lodash';
import BordereauEtat from '../../../constants/liquidation/bordereaux/BordereauEtat';
import BordereauEtatAcquittement from '../../../constants/liquidation/bordereaux/BordereauEtatAcquittement';
import BordereauStateType from '../../../constants/liquidation/bordereaux/BordereauStateType';
import CODE_BHM from '../../../constants/mouvements/codeBHM';
import { TYPE_BORDEREAU } from '../../../constants/mouvements/typeBordereau';
import Tdt from '../../../interfaces/common/Tdt';
import type { BordereauWithState } from '../../../interfaces/pieces/TreeRow';
import type BaseBordereauState from './BaseBordereauState';
import BordereauAcquitteState from './BordereauAcquitteState';
import BordereauArchiveState from './BordereauArchiveState';
import BordereauAttenteSignatureState from './BordereauAttenteSignatureState';
import BordereauAttenteTransmissionParapheurState from './BordereauAttenteTransmissionParapheurState';
import BordereauEnCoursState from './BordereauEnCoursState';
import BordereauEnEchecState from './BordereauEnEchecState';
import BordereauFluxEmisState from './BordereauFluxEmisState';
import BordereauInconnuState from './BordereauInconnuState';
import BordereauNonAcquitteState from './BordereauNonAcquitteState';
import BordereauNouveauState from './BordereauNouveauState';
import BordereauNumeroteState from './BordereauNumeroteState';
import BordereauPesEnInstanceState from './BordereauPesEnInstanceState';
import BordereauPrepareTdtState from './BordereauPrepareTdtState';
import BordereauRefuseState from './BordereauRefuseState';
import BordereauRejeteState from './BordereauRejeteState';
import BordereauTeletransmissionState from './BordereauTeletransmissionState';
import BordereauPrisEnChargeDGFIPState from './BordereauTraiteDGFIPState';
import BordereauTransmisComptableState from './BordereauTransmisComptableState';

// biome-ignore lint/complexity/noStaticOnlyClass: Je ne sais pas pourquoi c'est en static
class BordereauStateFactory {
	static getState(
		bordereau: Bordereau | BordereauWithState,
		exercice: Exercice,
	): BaseBordereauState {
		const stateType = BordereauStateFactory.getStateType(bordereau, exercice);
		switch (stateType) {
			case BordereauStateType.ACQUITTE:
				return new BordereauAcquitteState();
			case BordereauStateType.ATTENTE_SIGNATURE:
				return new BordereauAttenteSignatureState();
			case BordereauStateType.EN_COURS:
				return new BordereauEnCoursState();
			case BordereauStateType.EN_ECHEC:
				return new BordereauEnEchecState();
			case BordereauStateType.FLUX_EMIS:
				return new BordereauFluxEmisState();
			case BordereauStateType.NON_ACQUITTE:
				return new BordereauNonAcquitteState();
			case BordereauStateType.NOUVEAU:
				return new BordereauNouveauState();
			case BordereauStateType.NUMEROTE:
				return new BordereauNumeroteState();
			case BordereauStateType.REFUSE:
				return new BordereauRefuseState();
			case BordereauStateType.TELETRANSMIS:
				return new BordereauTeletransmissionState();
			case BordereauStateType.TRANSMIS_COMPTABLE:
				return new BordereauTransmisComptableState();
			case BordereauStateType.ARCHIVE:
				return new BordereauArchiveState();
			case BordereauStateType.REJETE:
				return new BordereauRejeteState();
			case BordereauStateType.PES_EN_INSTANCE:
				return new BordereauPesEnInstanceState();
			case BordereauStateType.ATTENTE_TRANSMISSION_PARAPHEUR:
				return new BordereauAttenteTransmissionParapheurState();
			case BordereauStateType.PREPARE_TDT:
				return new BordereauPrepareTdtState();
			case BordereauStateType.TRAITE_DGFIP:
				return new BordereauPrisEnChargeDGFIPState();
			default:
				return new BordereauInconnuState();
		}
	}

	static getStateType(
		bordereau: Bordereau | BordereauWithState,
		exercice: Exercice,
	): BordereauStateType {
		if (isNewIdentifiant(bordereau.identifiant)) {
			return BordereauStateType.NOUVEAU;
		}

		if (bordereau.codeBHM === CODE_BHM.VALUE.BROUILLARD) {
			return BordereauStateType.EN_COURS;
		}

		if (bordereau.codeBHM === CODE_BHM.VALUE.HISTORISE) {
			if (bordereau.acquitte === BordereauEtatAcquittement.ACQUITTE) {
				if (bordereau.retourPesOk) {
					return BordereauStateType.TRAITE_DGFIP;
				}
				return BordereauStateType.ACQUITTE;
			}

			if (
				bordereau.acquitte === BordereauEtatAcquittement.NON_ACQUITTE &&
				!isEmpty(bordereau.anomalieAck)
			) {
				return BordereauStateType.NON_ACQUITTE;
			}

			if (bordereau.etat === BordereauEtat.TRAITEMENT_PES_EN_INSTANCE) {
				return BordereauStateType.PES_EN_INSTANCE;
			}

			if (!bordereau.emis && bordereau.typePes === TYPE_BORDEREAU.REJET) {
				return BordereauStateType.ARCHIVE;
			}

			if (bordereau.etat === BordereauEtat.REJETE) {
				return BordereauStateType.REJETE;
			}

			if (!bordereau.emis) {
				return BordereauStateType.NUMEROTE;
			}

			if (
				!isEmpty(bordereau.motifEchec) ||
				bordereau.etat === BordereauEtat.ERREUR
			) {
				return BordereauStateType.EN_ECHEC;
			}

			if (
				!isEmpty(bordereau.iddX2) ||
				exercice.entite.tdtIxchange2 === Tdt.IXCHANGE2
			) {
				if (
					isEmpty(bordereau.etat) ||
					bordereau.etat === BordereauEtat.REJET_LEVE ||
					bordereau.etat === BordereauEtat.TRANSMIS_PARAPHEUR
				) {
					return BordereauStateType.ATTENTE_SIGNATURE;
				}

				if (bordereau.etat === BordereauEtat.REFUSE) {
					return BordereauStateType.REFUSE;
				}

				if (
					bordereau.etat === BordereauEtat.SIGNE ||
					bordereau.etat === BordereauEtat.TERMINE
				) {
					return BordereauStateType.TRANSMIS_COMPTABLE;
				}

				if (bordereau.etat === BordereauEtat.ATTENTE_TRANSMISSION_PARAPHEUR) {
					return BordereauStateType.ATTENTE_TRANSMISSION_PARAPHEUR;
				}

				if (bordereau.etat === BordereauEtat.PREPARE_TDT) {
					return BordereauStateType.PREPARE_TDT;
				}
			}

			if (!isEmpty(bordereau.dateEmission)) {
				if (
					isEmpty(exercice.entite.tdtIxchange2) ||
					exercice.entite.tdtIxchange2 === Tdt.PORTAIL
				) {
					return BordereauStateType.FLUX_EMIS;
				}
				return BordereauStateType.TELETRANSMIS;
			}
		}

		return BordereauStateType.INCONNU;
	}

	static getAllStates(): BaseBordereauState[] {
		return [
			new BordereauAcquitteState(),
			new BordereauArchiveState(),
			new BordereauAttenteSignatureState(),
			new BordereauAttenteTransmissionParapheurState(),
			new BordereauEnCoursState(),
			new BordereauEnEchecState(),
			new BordereauFluxEmisState(),
			new BordereauNonAcquitteState(),
			new BordereauNumeroteState(),
			new BordereauPrepareTdtState(),
			new BordereauPrisEnChargeDGFIPState(),
			new BordereauRefuseState(),
			new BordereauRejeteState(),
			new BordereauTeletransmissionState(),
			new BordereauTransmisComptableState(),
			new BordereauPesEnInstanceState(),
		].sort((a: BaseBordereauState, b: BaseBordereauState) => a.step - b.step);
	}
}

export default BordereauStateFactory;
